import { Seo } from "@/components/Seo";
import { PolicyLayout, PolicySection } from "@/components/PolicyLayout";

export default function PrivacyPolicy() {
  return (
    <>
      <Seo
        title="Privacy Policy — Symmori"
        description="How Symmori collects, uses, retains and protects personal information submitted through symmori.com, under UK GDPR and the Data Protection Act 2018."
        path="/privacy-policy"
      />
      <PolicyLayout
        overline="Legal"
        title="Privacy Policy"
        updated="July 2026"
        testid="privacy-policy-page"
        notice={[
          "Legal entity details: the registered legal name and trading address of the business operating Symmori should be confirmed and added to the “Who we are” section.",
          "ICO registration: confirm whether the business is registered with the Information Commissioner's Office and add the registration reference if applicable.",
          "Retention periods: the retention periods stated below are the business's stated policy — confirm they match actual practice.",
          "Analytics: this site is hosted on a platform that includes product analytics. Confirm the accuracy of the “Cookies, analytics and tracking” section once final hosting configuration is settled.",
        ]}
      >
        <PolicySection title="Who we are" testid="privacy-who">
          <p>
            Symmori is an influencer marketing agency specialising in informational
            YouTube, operating the website symmori.com. For the purposes of the UK
            General Data Protection Regulation (UK GDPR) and the Data Protection Act
            2018, Symmori is the data controller for personal information collected
            through this website.
          </p>
          <p>
            You can contact us about any privacy matter at{" "}
            <a href="mailto:admin@symmori.com" className="underline underline-offset-4 decoration-ink/30 hover:decoration-mutedblue" data-testid="privacy-contact-email">
              admin@symmori.com
            </a>
            .
          </p>
        </PolicySection>

        <PolicySection title="What personal information we collect" testid="privacy-what">
          <p>When you submit our contact form, we collect:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Your name;</li>
            <li>Your work email address;</li>
            <li>Your company name;</li>
            <li>Your company website, if you choose to provide it;</li>
            <li>The content of your message, including campaign objectives and any additional information you choose to share;</li>
            <li>Your IP address at the time of submission, used for spam prevention and security.</li>
          </ul>
          <p>
            Our hosting platform may also collect standard technical and usage
            information (such as device, browser and page-interaction data) through
            its built-in analytics. We do not collect special category data, and this
            website is not directed at children.
          </p>
        </PolicySection>

        <PolicySection title="Why we collect it and how it is used" testid="privacy-why">
          <p>We use the information above to:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Respond to your enquiry and manage our correspondence with you;</li>
            <li>Assess whether a sponsorship campaign is a genuine fit before entering into any agreement;</li>
            <li>Investigate reports of spam, impersonation or misuse of our name and domains;</li>
            <li>Protect the website against abuse, spam and automated misuse.</li>
          </ul>
          <p>
            We do not use your information for unrelated marketing, and we do not add
            enquiry submitters to any mailing list.
          </p>
        </PolicySection>

        <PolicySection title="Legal bases we rely on" testid="privacy-bases">
          <p>
            Under UK GDPR, we process personal information submitted through the
            contact form on the basis of:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong>Legitimate interests</strong> (Article 6(1)(f)) — responding to
              business enquiries addressed to us, and protecting our website, name and
              domains from abuse; and
            </li>
            <li>
              <strong>Steps prior to entering a contract</strong> (Article 6(1)(b)) —
              where your enquiry relates to a potential engagement and our
              correspondence is needed to explore or prepare that engagement.
            </li>
          </ul>
        </PolicySection>

        <PolicySection title="How long we keep information" testid="privacy-retention">
          <p>
            As a matter of business policy, contact form submissions and related
            correspondence are retained for up to 24 months after our last meaningful
            contact with you, unless a working relationship continues or a longer
            period is required to resolve a dispute or meet a legal obligation.
            Reports of abuse may be retained for up to 36 months so that patterns of
            misuse can be identified and addressed.
          </p>
        </PolicySection>

        <PolicySection title="Who we share it with" testid="privacy-sharing">
          <p>
            We do not sell personal information. We share it only with the service
            providers that operate this website and our communications, who act as
            processors on our behalf:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Our website hosting and database provider, which stores form submissions securely;</li>
            <li>Our transactional email provider, which delivers enquiry notifications to our inbox.</li>
          </ul>
          <p>
            We may also disclose information where required by law, or to establish,
            exercise or defend legal claims.
          </p>
        </PolicySection>

        <PolicySection title="International data transfers" testid="privacy-transfers">
          <p>
            Some of our service providers process data outside the United Kingdom.
            Where personal information is transferred internationally, we rely on
            appropriate safeguards recognised under UK data protection law, such as
            adequacy regulations or contractual protections (including the UK
            International Data Transfer Agreement or approved addenda), where these
            are required.
          </p>
        </PolicySection>

        <PolicySection title="Cookies, analytics and tracking" testid="privacy-cookies">
          <p>
            This website does not set first-party marketing or advertising cookies,
            and we do not run interest-based advertising trackers. Our hosting
            platform includes product analytics (PostHog) which may set cookies or
            use similar technologies to collect aggregated technical and usage
            information — such as pages visited, device and browser type — to help
            operate and improve the site. You can control or delete cookies through
            your browser settings at any time.
          </p>
        </PolicySection>

        <PolicySection title="Your rights" testid="privacy-rights">
          <p>
            Under UK GDPR and the Data Protection Act 2018, you have the right to:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Request access to the personal information we hold about you;</li>
            <li>Ask us to correct inaccurate or incomplete information;</li>
            <li>Ask us to erase your information in certain circumstances;</li>
            <li>Restrict or object to our processing of your information;</li>
            <li>Request a portable copy of information you have provided;</li>
            <li>Withdraw consent where processing is based on consent.</li>
          </ul>
          <p>
            To exercise any of these rights, email{" "}
            <a href="mailto:admin@symmori.com" className="underline underline-offset-4 decoration-ink/30 hover:decoration-mutedblue">
              admin@symmori.com
            </a>
            . We will respond within one month. You also have the right to complain to
            the Information Commissioner's Office (ICO), the UK supervisory authority,
            at ico.org.uk.
          </p>
        </PolicySection>

        <PolicySection title="Changes to this policy" testid="privacy-changes">
          <p>
            This policy is effective from July 2026. If we make material changes, we
            will update this page and revise the effective date. We encourage you to
            review it periodically. Questions about this policy can be sent to{" "}
            <a href="mailto:admin@symmori.com" className="underline underline-offset-4 decoration-ink/30 hover:decoration-mutedblue">
              admin@symmori.com
            </a>
            .
          </p>
        </PolicySection>
      </PolicyLayout>
    </>
  );
}
